import type { ChildId } from './valueObjects/childId';
import type { MeasureDateTime } from './valueObjects/measureDateTime';
import type { MeasurerId } from './valueObjects/measurerId';
import type { MeasurerType } from './valueObjects/measurerType';
import type { ParentId } from './valueObjects/parentId';
import type { Temperature } from './valueObjects/temperature';
import type { TemperatureJsonPub, TemperatureJsonPvt } from '@/types/domain/temperature/entities/temperatureEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 体温プロパティ非公開
 */
export type TemperaturePropsPvt = {
  /** 測定日時 */
  measureDateTime: MeasureDateTime,
  /** 測定者ID */
  measurerId: MeasurerId,
  /** 測定タイプ */
  measurerType: MeasurerType,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 体温 */
  temperature: Temperature,
}

/**
 * 体温プロパティ公開
 */
export type TemperaturePropsPub = Record<string, never>

/**
 * 体温Entity
 */
export class TemperatureEntity extends EntityPub<
  TemperaturePropsPvt,
  TemperatureJsonPvt,
  TemperaturePropsPub,
  TemperatureJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonData(): TemperatureJsonPvt {
    return {
      measureDateTime: this.measureDateTime.value.toJSON(),
      measurerId: this.measurerId.value,
      measurerType: this.measurerType.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      temperature: this.temperature.value,
    };
  }

  toJsonPubData(): TemperatureJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryTemperatureTemperatureEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.measureDateTime.toStringDateTime()}#${this.parentId.value}_${this.childId.value}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string {
    return `${this.parentId.value}_${this.childId.value}#${this.measureDateTime.toStringDateTime()}#${this.measurerType.value}#${this.measurerId.value}`;
  }

  /**
   * 親子ID
   */
  get parentChildId(): string {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 測定日時
   */
  get measureDateTime(): MeasureDateTime {
    return this._value.measureDateTime;
  }

  /**
   * 測定日時
   */
  set measureDateTime(measureDateTime: MeasureDateTime) {
    this._value.measureDateTime = measureDateTime;
  }

  /**
   * 体温
   */
  get temperature(): Temperature {
    return this._value.temperature;
  }

  /**
   * 体温
   */
  set temperature(temperature: Temperature) {
    this._value.temperature = temperature;
  }

  /**
   * 測定者ID
   */
  get measurerId(): MeasurerId {
    return this._value.measurerId;
  }

  /**
   * 測定者ID
   */
  set measurerId(measurerId: MeasurerId) {
    this._value.measurerId = measurerId;
  }

  /**
   * 測定タイプ
   */
  get measurerType(): MeasurerType {
    return this._value.measurerType;
  }

  /**
   * 測定タイプ
   */
  set measurerType(measurerType: MeasurerType) {
    this._value.measurerType = measurerType;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 親ID
   */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /**
   * 子ID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 子ID
   */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }
}
